import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GraduationCap, Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { LegalFooter } from '../components/LegalFooter';
import { API_URL } from '../utils/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <GraduationCap className="h-8 w-8 text-[#2C4C3B]" />
            <span className="font-outfit text-2xl font-semibold text-[#1C1C1C]">FrameEd</span>
          </Link>

          {sent ? (
            <div data-testid="forgot-password-success">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mb-6">
                <CheckCircle2 className="h-7 w-7 text-green-600" />
              </div>
              <h1 className="font-outfit text-2xl font-semibold text-[#1C1C1C] mb-2">Check your email</h1>
              <p className="text-[#595959] mb-8 leading-relaxed">
                If an account exists for <strong className="text-[#1C1C1C]">{email}</strong>, we've sent a password reset link. It expires in 1 hour.
              </p>
              <p className="text-sm text-[#595959] mb-6">
                Didn't receive the email? Check your spam folder, or{' '}
                <button onClick={() => { setSent(false); setEmail(''); }} className="text-[#2C4C3B] font-medium hover:underline">try again</button>.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full rounded-xl py-5 border-[#E5E5E5]" data-testid="back-to-login-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <div data-testid="forgot-password-form">
              <h1 className="font-outfit text-2xl font-semibold text-[#1C1C1C] mb-2">Forgot your password?</h1>
              <p className="text-[#595959] mb-8">Enter your email address and we'll send you a link to reset your password.</p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#1C1C1C]">Email address</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#595959]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 rounded-xl border-[#E5E5E5] py-5 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full bg-[#2C4C3B] hover:bg-[#1C3C2B] text-white rounded-xl py-5"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : 'Send Reset Link'}
                </Button>
              </form>

              <p className="mt-8 text-center text-[#595959]">
                Remember your password?{' '}
                <Link to="/login" className="text-[#2C4C3B] font-medium hover:underline">Sign in</Link>
              </p>
            </div>
          )}
        </div>
      </div>
      <LegalFooter variant="compact" />
    </div>
  );
}
